import { useEffect, useMemo, useState } from "react";

import { useTheme } from "next-themes";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useLocation, useNavigate, useParams } from "react-router-dom";

import { AdminLoginCard } from "@/components/admin/AdminLoginCard";
import { NumerolojiAdminMobileSidebar, NumerolojiAdminSidebar } from "@/components/admin/NumerolojiAdminSidebar";
import { NumerolojiActivityEditor, type ActivityDraft } from "@/components/admin/NumerolojiActivityEditor";
import { Button } from "@/components/ui/button";
import { adminFetch, useAdminSession } from "@/hooks/use-admin-session";

import { Bell } from "lucide-react";

type CatalogActivity = {
  id: string;
  title: string;
  description: string;
  metaLabel?: string;
  metaValue?: string;
  durationMinutes?: number;
  difficulty?: "easy" | "medium" | "hard";
  coverImageUrl?: string;
  iconKey?: string;
  featured?: boolean;
  active: boolean;
  isPremium?: boolean;
  tags: string[];
};

type CatalogProduct = {
  id: string;
  sku: string;
  name: string;
  priceTry: number;
  active: boolean;
};

type CatalogLinkedItem = {
  id: string;
  title: string;
  active: boolean;
};

type CatalogResponse = {
  ok: true;
  packages: unknown[];
  products: CatalogProduct[];
  meditations: CatalogLinkedItem[];
  diets: CatalogLinkedItem[];
  activities: CatalogActivity[];
  templates: unknown[];
  settings: unknown;
};

type CatalogBreathResponse = {
  ok: true;
  items: CatalogLinkedItem[];
};

async function fetchCatalog(): Promise<CatalogResponse> {
  const res = await adminFetch("/api/admin/numeroloji/catalog");
  const json = (await res.json().catch(() => null)) as any;
  if (!res.ok) {
    throw new Error(typeof json?.error === "string" ? json.error : "Katalog alınamadı.");
  }
  return json as CatalogResponse;
}

async function saveActivity(payload: Partial<CatalogActivity> & { title: string }) {
  const res = await adminFetch("/api/admin/numeroloji/activities", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(payload),
  });
  const json = (await res.json().catch(() => null)) as any;
  if (!res.ok) {
    throw new Error(typeof json?.error === "string" ? json.error : "Aktivite kaydedilemedi.");
  }
  return json as { ok: true; item: CatalogActivity };
}

async function fetchAdminBreaths(): Promise<CatalogLinkedItem[]> {
  const res = await adminFetch("/api/admin/numeroloji/breaths");
  const json = (await res.json().catch(() => null)) as any;
  if (!res.ok) {
    throw new Error(typeof json?.error === "string" ? json.error : "Nefes listesi alınamadı.");
  }
  const data = json as CatalogBreathResponse;
  return Array.isArray(data?.items) ? data.items : [];
}

function normalizeTag(value: string) {
  return String(value ?? "")
    .trim()
    .toLocaleLowerCase("tr-TR")
    .replace(/\s+/g, "")
    .replace(/[ç]/g, "c")
    .replace(/[ğ]/g, "g")
    .replace(/[ı]/g, "i")
    .replace(/[ö]/g, "o")
    .replace(/[ş]/g, "s")
    .replace(/[ü]/g, "u")
    .replace(/[^0-9a-z]+/g, "");
}

function hasRituelTag(tags: string[]) {
  const values = new Set((tags ?? []).map((tag) => normalizeTag(tag)).filter(Boolean));
  return values.has("rituel") || values.has("ritueluygulamasi") || values.has("ritual");
}

function ensureRituelTag(tags: string[]) {
  const normalized = new Set((tags ?? []).map((tag) => normalizeTag(tag)).filter(Boolean));
  const cleaned = (tags ?? []).map((tag) => String(tag ?? "").trim()).filter(Boolean);
  if (!normalized.has("rituel")) cleaned.push("rituel");
  return Array.from(new Set(cleaned));
}

const RITUAL_TAG_PREFIX = {
  intent: "rituel_niyet:",
  material: "rituel_malzeme:",
  step: "rituel_adim:",
  stepImage: "rituel_adim_gorsel:",
  points: "rituel_puan:",
  programDays: "rituel_program_gun:",
  product: "rituel_urun:",
  scheduleTime: "rituel_saat:",
} as const;

const ACTIVITY_RELATION_TAG_PREFIX = {
  kind: "gorev_tur:",
  target: "gorev_hedef:",
} as const;

const ACTIVITY_POINTS_TAG_PREFIXES = ["gorev_puan:", "task_puan:"] as const;

type ActivityRelationKind = "" | "meditasyon" | "nefes" | "diyet" | "rituel";

function decodeTagValue(raw: string) {
  try {
    return decodeURIComponent(raw);
  } catch {
    return raw;
  }
}

function encodeTagValue(raw: string) {
  return encodeURIComponent(String(raw ?? "").trim());
}

function parseRituelConfigFromTags(tags: string[]) {
  const intent =
    tags
      .find((tag) => String(tag ?? "").startsWith(RITUAL_TAG_PREFIX.intent))
      ?.slice(RITUAL_TAG_PREFIX.intent.length) ?? "";

  const pointsRaw =
    tags
      .find((tag) => String(tag ?? "").startsWith(RITUAL_TAG_PREFIX.points))
      ?.slice(RITUAL_TAG_PREFIX.points.length) ?? "";

  const programDaysRaw =
    tags
      .find((tag) => String(tag ?? "").startsWith(RITUAL_TAG_PREFIX.programDays))
      ?.slice(RITUAL_TAG_PREFIX.programDays.length) ?? "";

  const materialTags = tags.filter((tag) => String(tag ?? "").startsWith(RITUAL_TAG_PREFIX.material));
  let materials: string[] = [];
  if (materialTags.length === 1) {
    materials = decodeTagValue(materialTags[0].slice(RITUAL_TAG_PREFIX.material.length))
      .split(",").map((m) => m.trim()).filter(Boolean);
  } else if (materialTags.length > 1) {
    materials = materialTags.map((t) => decodeTagValue(String(t).slice(RITUAL_TAG_PREFIX.material.length))).filter(Boolean);
  }

  const stepTags = tags.filter((tag) => String(tag ?? "").startsWith(RITUAL_TAG_PREFIX.step));
  let steps: string[] = [];
  if (stepTags.length === 1) {
    const val = decodeTagValue(stepTags[0].slice(RITUAL_TAG_PREFIX.step.length));
    steps = val.includes("|") ? val.split("|").map((s) => s.trim()).filter(Boolean) : [val].filter(Boolean);
  } else if (stepTags.length > 1) {
    steps = stepTags.map((t) => decodeTagValue(String(t).slice(RITUAL_TAG_PREFIX.step.length))).filter(Boolean);
  }

  const stepImagesTag = tags.find((tag) => String(tag ?? "").startsWith(RITUAL_TAG_PREFIX.stepImage));
  const stepImages: string[] = stepImagesTag
    ? stepImagesTag.slice(RITUAL_TAG_PREFIX.stepImage.length).split("|")
    : [];

  const productIds = tags
    .filter((tag) => String(tag ?? "").startsWith(RITUAL_TAG_PREFIX.product))
    .map((tag) => String(tag).slice(RITUAL_TAG_PREFIX.product.length).trim())
    .filter(Boolean);

  const scheduleTimeRaw =
    tags
      .find((tag) => String(tag ?? "").startsWith(RITUAL_TAG_PREFIX.scheduleTime))
      ?.slice(RITUAL_TAG_PREFIX.scheduleTime.length) ?? "";

  return {
    intent: decodeTagValue(intent),
    points: pointsRaw,
    programDays: programDaysRaw,
    materials,
    steps,
    stepImages,
    productIds: Array.from(new Set(productIds)),
    scheduleTime: scheduleTimeRaw,
  };
}

function stripRituelConfigTags(tags: string[]) {
  return (tags ?? []).filter((tag) => {
    const v = String(tag ?? "");
    return (
      !v.startsWith(RITUAL_TAG_PREFIX.intent) &&
      !v.startsWith(RITUAL_TAG_PREFIX.material) &&
      !v.startsWith(RITUAL_TAG_PREFIX.step) &&
      !v.startsWith(RITUAL_TAG_PREFIX.stepImage) &&
      !v.startsWith(RITUAL_TAG_PREFIX.points) &&
      !v.startsWith(RITUAL_TAG_PREFIX.programDays) &&
      !v.startsWith(RITUAL_TAG_PREFIX.product) &&
      !v.startsWith(RITUAL_TAG_PREFIX.scheduleTime)
    );
  });
}

function parsePositiveNumberTag(tags: string[], prefixes: readonly string[]) {
  const raw =
    (tags ?? [])
      .map((tag) => String(tag ?? "").trim())
      .find((tag) => prefixes.some((prefix) => tag.startsWith(prefix))) ?? "";

  const prefix = prefixes.find((item) => raw.startsWith(item));
  if (!prefix) return 0;

  const parsed = Number(raw.slice(prefix.length));
  return Number.isFinite(parsed) && parsed > 0 ? Math.round(parsed) : 0;
}

function parseActivityPointsFromTags(tags: string[]) {
  return parsePositiveNumberTag(tags, ACTIVITY_POINTS_TAG_PREFIXES);
}

function parseActivityRelationFromTags(tags: string[]): { kind: ActivityRelationKind; targetId: string } {
  const rawTags = (tags ?? []).map((tag) => String(tag ?? "").trim()).filter(Boolean);

  const kindFromPrefixRaw = rawTags.find((tag) => tag.startsWith(ACTIVITY_RELATION_TAG_PREFIX.kind))?.slice(ACTIVITY_RELATION_TAG_PREFIX.kind.length) ?? "";
  const kindNormalized = normalizeTag(kindFromPrefixRaw);
  const kindFromPrefix: ActivityRelationKind =
    kindNormalized === "meditasyon" || kindNormalized === "meditation"
      ? "meditasyon"
      : kindNormalized === "nefes" || kindNormalized === "breath"
        ? "nefes"
        : kindNormalized === "diyet" || kindNormalized === "diet"
          ? "diyet"
          : kindNormalized === "rituel" || kindNormalized === "ritual"
            ? "rituel"
            : "";

  const targetFromPrefix = rawTags.find((tag) => tag.startsWith(ACTIVITY_RELATION_TAG_PREFIX.target))?.slice(ACTIVITY_RELATION_TAG_PREFIX.target.length) ?? "";

  if (kindFromPrefix) {
    return { kind: kindFromPrefix, targetId: targetFromPrefix.trim() };
  }

  const fallback = rawTags.find((tag) => {
    const lower = tag.toLocaleLowerCase("tr-TR");
    return lower.startsWith("meditasyon:") || lower.startsWith("meditation:") || lower.startsWith("nefes:") || lower.startsWith("breath:") || lower.startsWith("diyet:") || lower.startsWith("diet:") || lower.startsWith("rituel:") || lower.startsWith("ritual:");
  });

  if (!fallback) return { kind: "", targetId: "" };

  const [typeRaw, ...rest] = fallback.split(":");
  const type = normalizeTag(typeRaw);
  const targetId = rest.join(":").trim();

  if (type === "meditasyon" || type === "meditation") return { kind: "meditasyon", targetId };
  if (type === "nefes" || type === "breath") return { kind: "nefes", targetId };
  if (type === "diyet" || type === "diet") return { kind: "diyet", targetId };
  if (type === "rituel" || type === "ritual") return { kind: "rituel", targetId };

  return { kind: "", targetId: "" };
}

function stripActivityPointsTags(tags: string[]) {
  return (tags ?? []).filter((tag) => {
    const raw = String(tag ?? "").trim();
    return !ACTIVITY_POINTS_TAG_PREFIXES.some((prefix) => raw.startsWith(prefix));
  });
}

function stripActivityRelationTags(tags: string[]) {
  return (tags ?? []).filter((tag) => {
    const raw = String(tag ?? "").trim();
    const lower = raw.toLocaleLowerCase("tr-TR");
    return (
      !raw.startsWith(ACTIVITY_RELATION_TAG_PREFIX.kind) &&
      !raw.startsWith(ACTIVITY_RELATION_TAG_PREFIX.target) &&
      !lower.startsWith("meditasyon:") &&
      !lower.startsWith("meditation:") &&
      !lower.startsWith("nefes:") &&
      !lower.startsWith("breath:") &&
      !lower.startsWith("diyet:") &&
      !lower.startsWith("diet:") &&
      !lower.startsWith("rituel:") &&
      !lower.startsWith("ritual:")
    );
  });
}

function buildActivityRelationTags(input: { kind?: ActivityRelationKind; targetId?: string }) {
  const kind = String(input.kind ?? "").trim() as ActivityRelationKind;
  const targetId = String(input.targetId ?? "").trim();
  if (!kind) return [] as string[];

  const tags = [`${ACTIVITY_RELATION_TAG_PREFIX.kind}${kind}`];
  if (targetId) {
    tags.push(`${ACTIVITY_RELATION_TAG_PREFIX.target}${targetId}`);
    tags.push(`${kind}:${targetId}`);
  }

  tags.push(kind);
  return Array.from(new Set(tags));
}

function buildActivityPointsTags(input: { activityPoints?: string }) {
  const rawPoints = String(input.activityPoints ?? "").trim();
  const parsedPoints = Number(rawPoints);
  if (!Number.isFinite(parsedPoints) || parsedPoints <= 0) return [] as string[];
  return [`gorev_puan:${Math.round(parsedPoints)}`];
}

function buildRituelConfigTags(input: {
  ritualIntent?: string;
  ritualMaterials?: string[];
  ritualSteps?: { text: string; imageUrl: string }[];
  ritualPoints?: string;
  ritualProgramDays?: string;
  ritualProductIds?: string[];
  ritualScheduleTime?: string;
}) {
  const tags: string[] = [];

  const intent = String(input.ritualIntent ?? "").trim();
  if (intent) tags.push(`${RITUAL_TAG_PREFIX.intent}${encodeTagValue(intent)}`);

  const points = String(input.ritualPoints ?? "").trim();
  const parsedPoints = Number(points);
  if (Number.isFinite(parsedPoints) && parsedPoints > 0) {
    tags.push(`${RITUAL_TAG_PREFIX.points}${Math.round(parsedPoints)}`);
  }

  const programDays = String(input.ritualProgramDays ?? "").trim();
  const parsedProgramDays = Number(programDays);
  if (Number.isFinite(parsedProgramDays) && parsedProgramDays > 0) {
    tags.push(`${RITUAL_TAG_PREFIX.programDays}${Math.round(parsedProgramDays)}`);
  }

  const materials = (input.ritualMaterials ?? []).map((m) => m.trim()).filter(Boolean);
  if (materials.length > 0) {
    tags.push(`${RITUAL_TAG_PREFIX.material}${encodeTagValue(materials.join(","))}`);
  }

  const steps = (input.ritualSteps ?? []).map((s) => s.text.trim()).filter(Boolean);
  if (steps.length > 0) {
    tags.push(`${RITUAL_TAG_PREFIX.step}${encodeTagValue(steps.join("|"))}`);
    const stepImages = (input.ritualSteps ?? []).map((s) => s.imageUrl?.trim() ?? "");
    if (stepImages.some(Boolean)) {
      tags.push(`${RITUAL_TAG_PREFIX.stepImage}${stepImages.join("|")}`);
    }
  }

  const productIds = Array.isArray(input.ritualProductIds)
    ? input.ritualProductIds.map((value) => String(value ?? "").trim()).filter(Boolean)
    : [];
  Array.from(new Set(productIds)).forEach((id) => tags.push(`${RITUAL_TAG_PREFIX.product}${id}`));

  const scheduleTime = String(input.ritualScheduleTime ?? "").trim();
  if (/^([01]\d|2[0-3]):([0-5]\d)$/.test(scheduleTime)) {
    tags.push(`${RITUAL_TAG_PREFIX.scheduleTime}${scheduleTime}`);
  }

  return tags;
}

function emptyDraft(isRituelMode: boolean): ActivityDraft {
  return {
    title: "",
    description: "",
    metaLabel: "",
    metaValue: "",
    durationMinutes: "",
    difficulty: "",
    coverImageUrl: "",
    iconKey: isRituelMode ? "meditation" : "",
    featured: false,
    active: true,
    isPremium: false,
    tagsText: isRituelMode ? "rituel, genel" : "genel",
    ritualIntent: "",
    ritualMaterials: [],
    ritualSteps: [],
    ritualPoints: "",
    ritualProgramDays: "",
    ritualProductIds: [],
    ritualScheduleTime: "",
    activityPoints: "",
    relationKind: "",
    relationItemId: "",
    price: "0",
    store_product_id: null,
    kategori_id: undefined,
    title_en: "",
    title_es: "",
    description_en: "",
    description_es: "",
  };
}

export default function AdminNumerolojiActivity() {
  const params = useParams();
  const activityId = params.id ? String(params.id).trim() : "";
  const isNew = !activityId;

  const navigate = useNavigate();
  const { pathname } = useLocation();
  const isRituelModeFromPath = pathname.startsWith("/admin/numeroloji/rituel");
  const queryClient = useQueryClient();

  const { theme, setTheme, systemTheme } = useTheme();
  const resolvedTheme = theme === "system" ? systemTheme : theme;
  const isDark = resolvedTheme === "dark";

  const { sessionQuery, loginMutation, logoutMutation } = useAdminSession();
  const configured = Boolean(sessionQuery.data?.configured);
  const authenticated = Boolean(sessionQuery.data?.authenticated);

  const catalogQuery = useQuery({
    queryKey: ["admin-numeroloji-catalog"],
    queryFn: fetchCatalog,
    enabled: authenticated,
    staleTime: 30_000,
  });

  const editingItem = useMemo(() => {
    if (isNew || !activityId) return null;
    return (catalogQuery.data?.activities ?? []).find((a) => a.id === activityId) ?? null;
  }, [catalogQuery.data, activityId, isNew]);

  const isRituelMode =
    isRituelModeFromPath ||
    (editingItem?.tags ?? []).some(
      (t) => String(t).toLowerCase().includes("rituel") || String(t).toLowerCase().includes("ritual")
    );

  const activities = useMemo(() => {
    const all = catalogQuery.data?.activities ?? [];
    return all.filter((activity) => (isRituelMode ? hasRituelTag(activity.tags ?? []) : !hasRituelTag(activity.tags ?? [])));
  }, [catalogQuery.data, isRituelMode]);

  const breathsQuery = useQuery({
    queryKey: ["admin-numeroloji-breaths"],
    queryFn: fetchAdminBreaths,
    enabled: authenticated && !isRituelMode,
    staleTime: 30_000,
  });

  const ritualProducts = useMemo(() => {
    return (catalogQuery.data?.products ?? [])
      .filter((product) => product?.id)
      .map((product) => ({
        id: product.id,
        name: product.name,
        sku: product.sku,
        priceTry: Number(product.priceTry) || 0,
        active: Boolean(product.active),
      }))
      .sort((a, b) => Number(b.active) - Number(a.active) || a.name.localeCompare(b.name, "tr"));
  }, [catalogQuery.data]);

  const [draft, setDraft] = useState<ActivityDraft>(() => emptyDraft(isRituelMode));
  const [seeded, setSeeded] = useState(false);

  const relationOptions = useMemo(() => {
    const meditations = (catalogQuery.data?.meditations ?? []).map((item) => ({ id: String(item.id), title: String(item.title), active: Boolean(item.active) }));
    const diets = (catalogQuery.data?.diets ?? []).map((item) => ({ id: String(item.id), title: String(item.title), active: Boolean(item.active) }));
    const breaths = (breathsQuery.data ?? []).map((item) => ({ id: String(item.id), title: String(item.title), active: Boolean(item.active) }));
    const rituals = (catalogQuery.data?.activities ?? [])
      .filter((item) => hasRituelTag(item.tags ?? []))
      .map((item) => ({ id: item.id, title: item.title, active: item.active }));

    return {
      meditasyon: meditations.sort((a, b) => Number(b.active) - Number(a.active) || a.title.localeCompare(b.title, "tr")),
      nefes: breaths.sort((a, b) => Number(b.active) - Number(a.active) || a.title.localeCompare(b.title, "tr")),
      diyet: diets.sort((a, b) => Number(b.active) - Number(a.active) || a.title.localeCompare(b.title, "tr")),
      rituel: rituals.sort((a, b) => Number(b.active) - Number(a.active) || a.title.localeCompare(b.title, "tr")),
    } as const;
  }, [activities, breathsQuery.data, catalogQuery.data?.diets, catalogQuery.data?.meditations]);

  useEffect(() => {
    if (!authenticated) return;

    if (isNew) {
      if (!seeded) {
        setDraft(emptyDraft(isRituelMode));
        setSeeded(true);
      }
      return;
    }

    if (!catalogQuery.data || seeded) return;

    const item = activities.find((a) => a.id === activityId);
    if (!item) {
      setSeeded(true);
      return;
    }

    const parsedRituel = parseRituelConfigFromTags(item.tags ?? []);
    const parsedRelation = parseActivityRelationFromTags(item.tags ?? []);
    const parsedActivityPoints = parseActivityPointsFromTags(item.tags ?? []);
    const visibleTags = stripActivityPointsTags(stripActivityRelationTags(stripRituelConfigTags(isRituelMode ? ensureRituelTag(item.tags ?? []) : item.tags ?? [])));

    setDraft({
      id: item.id,
      title: item.title,
      description: item.description,
      metaLabel: item.metaLabel ?? "",
      metaValue: item.metaValue ?? "",
      durationMinutes: item.durationMinutes === undefined ? "" : String(item.durationMinutes),
      difficulty: (item.difficulty ?? "") as any,
      coverImageUrl: item.coverImageUrl ?? "",
      iconKey: (item.iconKey ?? "") as any,
      featured: Boolean(item.featured),
      active: item.active,
      isPremium: item.isPremium ?? false,
      tagsText: visibleTags.join(", "),
      ritualIntent: parsedRituel.intent,
      ritualMaterials: parsedRituel.materials,
      ritualSteps: parsedRituel.steps.map((text, i) => ({ text, imageUrl: parsedRituel.stepImages[i] ?? "" })),
      ritualPoints: parsedRituel.points,
      ritualProgramDays: parsedRituel.programDays,
      ritualProductIds: parsedRituel.productIds,
      ritualScheduleTime: parsedRituel.scheduleTime,
      activityPoints: parsedActivityPoints > 0 ? String(parsedActivityPoints) : "",
      relationKind: isRituelMode ? "rituel" : parsedRelation.kind,
      relationItemId: parsedRelation.targetId,
      price: String((item as any).price ?? 0),
      store_product_id: (item as any).store_product_id ?? null,
      kategori_id: (item as any).kategori_id ?? undefined,
      title_en: (item as any).title_en ?? "",
      title_es: (item as any).title_es ?? "",
      description_en: (item as any).description_en ?? "",
      description_es: (item as any).description_es ?? "",
    });
    setSeeded(true);
  }, [authenticated, activityId, activities, catalogQuery.data, isNew, isRituelMode, seeded]);

  const mutation = useMutation({
    mutationFn: (payload: any) => {
      const baseTags = Array.isArray(payload?.tags) ? payload.tags : [];
      const cleanTags = stripActivityPointsTags(stripActivityRelationTags(stripRituelConfigTags(baseTags)));
      const ritualConfigTags = isRituelMode
        ? buildRituelConfigTags({
            ritualIntent: payload?.ritualIntent,
            ritualMaterials: payload?.ritualMaterials,
            ritualSteps: payload?.ritualSteps,
            ritualPoints: payload?.ritualPoints,
            ritualProgramDays: payload?.ritualProgramDays,
            ritualProductIds: payload?.ritualProductIds,
            ritualScheduleTime: payload?.ritualScheduleTime,
          })
        : [];

      const relationTags = buildActivityRelationTags({
        kind: isRituelMode ? "rituel" : (payload?.relationKind as ActivityRelationKind),
        targetId: payload?.relationItemId,
      });
      const activityPointsTags = buildActivityPointsTags({
        activityPoints: payload?.activityPoints,
      });

      return saveActivity({
        ...payload,
        tags: isRituelMode
          ? ensureRituelTag([...cleanTags, ...ritualConfigTags, ...relationTags])
          : [...cleanTags, ...activityPointsTags, ...relationTags],
      });
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ["admin-numeroloji-catalog"] });
      navigate(isRituelMode ? "/admin/numeroloji/rituel" : "/admin/numeroloji/aktivite", { replace: true });
    },
  });

  if (sessionQuery.isLoading) {
    return <div className="text-sm text-muted-foreground">Yükleniyor...</div>;
  }

  if (!authenticated) {
    return (
      <AdminLoginCard
        configured={configured}
        onLogin={(args) => loginMutation.mutate(args)}
        isLoading={loginMutation.isPending}
        error={loginMutation.isError ? (loginMutation.error as Error).message : undefined}
      />
    );
  }

  if (!isNew && catalogQuery.isLoading) {
    return <div className="text-sm text-muted-foreground">Yükleniyor...</div>;
  }

  if (!isNew && catalogQuery.isError) {
    return <div className="text-sm text-destructive">{(catalogQuery.error as Error).message}</div>;
  }

  if (!isNew && catalogQuery.data && !activities.some((a) => a.id === activityId)) {
    return (
      <div className="p-4 sm:p-6">
        <div className="rounded-2xl border border-destructive/30 bg-destructive/10 p-4 text-sm text-destructive">
          Aktivite bulunamadı.
        </div>
        <div className="mt-4">
          <Button className="rounded-full" onClick={() => navigate(isRituelMode ? "/admin/numeroloji/rituel" : "/admin/numeroloji/aktivite", { replace: true })}>
            {isRituelMode ? "Ritüellere dön" : "Aktivitelere dön"}
          </Button>
        </div>
      </div>
    );
  }

  const headerTitle = isRituelMode ? (isNew ? "Yeni Ritüel" : "Ritüel Düzenle") : isNew ? "Yeni Aktivite" : "Aktivite Düzenle";

  return (
    <div className="flex min-h-dvh w-full">
      <NumerolojiAdminSidebar mode="links" isDark={isDark} onToggleTheme={() => setTheme(isDark ? "light" : "dark")} />

      <div className="flex min-w-0 flex-1 flex-col bg-slate-100/60 dark:bg-slate-900/30">
        <header className="h-16 border-b border-slate-200/70 bg-white/70 px-4 backdrop-blur dark:border-slate-800/60 dark:bg-slate-950/40 sm:px-6">
          <div className="flex h-full items-center justify-between gap-3">
            <div className="flex items-center gap-3">
              <NumerolojiAdminMobileSidebar
                mode="links"
                isDark={isDark}
                onToggleTheme={() => setTheme(isDark ? "light" : "dark")}
              />
              <div className="text-base font-semibold tracking-tight text-slate-900 dark:text-slate-50 sm:text-lg">
                {headerTitle}
              </div>
            </div>

            <div className="flex items-center gap-3">
              <button
                type="button"
                className="grid h-10 w-10 place-items-center rounded-xl border border-slate-200 bg-white text-slate-600 hover:bg-slate-50 dark:border-slate-800 dark:bg-slate-950/40 dark:text-slate-300"
                aria-label="Bildirimler"
              >
                <Bell className="h-4 w-4" />
              </button>

              <Button
                variant="outline"
                className="rounded-full"
                size="sm"
                onClick={() => logoutMutation.mutate()}
                disabled={logoutMutation.isPending}
              >
                Çıkış
              </Button>
            </div>
          </div>
        </header>

        <div className="min-w-0 flex-1 p-4 sm:p-6">
          <NumerolojiActivityEditor
            draft={draft}
            setDraft={setDraft}
            isSaving={mutation.isPending}
            isRituelMode={isRituelMode}
            ritualProducts={ritualProducts}
            relationOptions={relationOptions}
            onClose={() => navigate(isRituelMode ? "/admin/numeroloji/rituel" : "/admin/numeroloji/aktivite", { replace: true })}
            onSave={(payload) => mutation.mutate(payload)}
          />

          {mutation.isError ? (
            <div className="mt-4 rounded-2xl border border-destructive/30 bg-destructive/10 p-4 text-sm text-destructive">
              {(mutation.error as Error).message}
            </div>
          ) : null}
        </div>
      </div>
    </div>
  );
}
